import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: searchParams.get('role') || 'learner',
    bio: '',
    expertise: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let user;
      if (isLogin) {
        user = await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        user = await register(formData);
        toast.success('Account created successfully!');
      }
      
      // Use the role from the returned user object
      if (user.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (user.role === 'instructor') {
        navigate('/instructor-dashboard');
      } else {
        navigate('/marketplace');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset link');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="text-center mb-8">
            <div onClick={() => navigate('/')} className="text-3xl font-bold tracking-tight cursor-pointer inline-flex items-center gap-0 mb-4" data-testid="logo">
              <img src="/logo.png" alt="NeoMaestro" className="h-10 w-10 object-contain" />
              <span className="text-gray-900">NeoMaestro</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="forgot-password-title">
              {forgotSent ? 'Check Your Email' : 'Forgot Password?'}
            </h1>
            <p className="text-gray-600 mt-2">
              {forgotSent
                ? 'We sent a password reset link to your email'
                : "Enter your email and we'll send you a reset link"}
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
            {forgotSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-[#34c759]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-[#34c759]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                </div>
                <p className="text-gray-600 mb-6">
                  If an account exists for <strong>{forgotEmail}</strong>, you'll receive a password reset link shortly.
                </p>
                <Button
                  onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                  className="w-full bg-[#34c759] text-white hover:bg-[#28a745] rounded-full py-6 text-lg font-bold"
                  data-testid="back-to-signin-btn"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div>
                  <Label htmlFor="forgot-email" className="text-gray-900">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="mt-2 bg-white border-gray-200 text-gray-900"
                    placeholder="Enter your registered email"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-[#34c759] text-white hover:bg-[#28a745] rounded-full py-6 text-lg font-bold"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            )}
            {!forgotSent && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="text-[#00a3af] hover:text-[#004d7a]"
                  data-testid="back-to-login-link"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div
            onClick={() => navigate('/')}
            className="text-3xl font-bold tracking-tight cursor-pointer inline-flex items-center gap-0 mb-4"
            data-testid="logo"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-10 w-10 object-contain" />
            <span className="text-gray-900">NeoMaestro</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="auth-title">{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
          <p className="text-gray-600 mt-2">
            {isLogin ? "Sign in to continue your learning journey" : "Join the learning community"}
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-gray-900">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-white border-gray-200 text-gray-900 focus:border-[#CCFF00]"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-gray-900">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 bg-white border-gray-200 text-gray-900 focus:border-[#CCFF00]"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-900">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 bg-white border-gray-200 text-gray-900 focus:border-[#CCFF00]"
                data-testid="password-input"
              />
              {isLogin && (
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#00a3af] hover:text-[#004d7a]"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="role" className="text-gray-900">I am a...</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="mt-2 bg-white border-gray-200 text-gray-900" data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="learner">Learner</SelectItem>
                      <SelectItem value="instructor">Instructor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role === 'instructor' && (
                  <>
                    <div>
                      <Label htmlFor="expertise" className="text-gray-900">Expertise</Label>
                      <Input
                        id="expertise"
                        type="text"
                        placeholder="e.g., AI, Finance, Marketing"
                        value={formData.expertise}
                        onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                        className="mt-2 bg-white border-gray-200 text-gray-900 focus:border-[#CCFF00]"
                        data-testid="expertise-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bio" className="text-gray-900">Bio</Label>
                      <Textarea
                        id="bio"
                        placeholder="Tell us about yourself..."
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        className="mt-2 bg-white border-gray-200 text-gray-900 focus:border-[#CCFF00]"
                        rows={3}
                        data-testid="bio-input"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#34c759] text-black hover:bg-[#28a745] rounded-full py-6 text-lg font-bold"
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#00a3af] hover:text-[#00a3af]"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}